import { Divider, List, ListItemText, Popover, Typography } from "@mui/material";
import { PopoverPosition } from "@mui/material/Popover";
import { defineMessage, FormattedMessage, MessageDescriptor } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MediaParticipant } from "@dvrt/web-phone-client";
import { useParticipantDisplayName } from "../hooks";
import { useFillModeHandler, useMuteHandler, usePinHandler, useUnmuteEnableHandler } from "./hooks";
import { useParticipantStatus } from "../participantStatus";
import { useCurrentMeeting } from "../../contexts/currentMeeting";
import { DenseListItemIcon } from "conference/components/widgets";

import ListItemButton from "@mui/material/ListItemButton";

type Props = {
    position: PopoverPosition;
    onClose: () => void;
    participant: MediaParticipant;
};

const StateIcon = ({ checked }: { checked: boolean }) => (
    <FontAwesomeIcon
        icon={checked ? "check-circle" : { iconName: "circle", prefix: "far" }}
        style={{ fontSize: "1.1em", opacity: checked ? 1 : 0.6 }}
    />
);

type StateListItemProps = {
    onClick: () => void;
    checked: boolean;
    itemLabel: MessageDescriptor;
    disabled?: boolean;
};

const StateListItem = ({ onClick, checked, itemLabel, disabled }: StateListItemProps) => (
    <ListItemButton onClick={onClick} dense disabled={disabled}>
        <DenseListItemIcon>
            <StateIcon checked={checked} />
        </DenseListItemIcon>
        <ListItemText>
            <FormattedMessage {...itemLabel} />
        </ListItemText>
    </ListItemButton>
);

const ParticipantMenu = ({ participant, position, onClose }: Props) => {
    const displayName = useParticipantDisplayName(participant);
    const currentMeeting = useCurrentMeeting();
    const { muted, toggleMute } = useMuteHandler(participant.cname);
    const { pinned, togglePin } = usePinHandler(participant.cname, participant.screen);
    const { fitted, toggleFitted } = useFillModeHandler(participant.cname, participant.screen);
    const { isHost } = useParticipantStatus(currentMeeting.selfUri);
    const { unmuteAllowed, toggleUnmuteAllow } = useUnmuteEnableHandler(participant.cname);

    const isSelf = participant.cname === currentMeeting.selfUri;

    return (
        <Popover
            open={!!position}
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={position}
            transformOrigin={{
                vertical: "top",
                horizontal: "left",
            }}
        >
            <div style={{ minWidth: 200 }}>
                <Typography style={{ padding: "1em" }}>{displayName}</Typography>
                <Divider />
                <List>
                    {isHost && (
                        <>
                            <StateListItem
                                onClick={toggleMute}
                                disabled={muted}
                                checked={muted}
                                itemLabel={defineMessage({ id: "muted", defaultMessage: "Muted" })}
                            />
                            {!isSelf && (
                                <StateListItem
                                    onClick={toggleUnmuteAllow}
                                    checked={unmuteAllowed}
                                    itemLabel={defineMessage({ id: "unmuteAllowed", defaultMessage: "Unmute allowed" })}
                                />
                            )}
                        </>
                    )}
                    {!isSelf && (
                        <StateListItem
                            onClick={togglePin}
                            checked={pinned}
                            itemLabel={defineMessage({ id: "pinned", defaultMessage: "Pinned" })}
                        />
                    )}
                    <StateListItem
                        onClick={toggleFitted}
                        checked={fitted}
                        itemLabel={defineMessage({ id: "fit", defaultMessage: "Fit" })}
                    />
                </List>
            </div>
        </Popover>
    );
};

export default ParticipantMenu;
